<template>
  <FormWrapper
    wrapperClass="margin-top-16 max-height-percent-97"
    bodyClass="padding-x-16 padding-top-16"
    :handleSubmit="handleSubmit"
    :loading="loading"
  >
    <Dropdown
      :title="$lang.select_term"
      :label="$lang.select_term"
      :placeholder="$lang.select_term"
      :value="form.termSelected"
      name="termSelected"
      id="termSelected"
      :vuelidateObj="$v.form.termSelected"
      :options="termOptions"
      :loading="termLoading"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
    <Input
      :title="$lang.name"
      :label="$lang.name"
      :placeholder="$lang.name"
      :value="form.name"
      name="name"
      id="name"
      type="text"
      :minLength="lengthNumber3"
      :maxLength="lengthNumber64"
      :vuelidateObj="$v.form.name"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
    <Input
      :title="$lang.family"
      :label="$lang.family"
      :placeholder="$lang.family"
      :value="form.family"
      name="family"
      id="family"
      type="text"
      :minLength="lengthNumber3"
      :maxLength="lengthNumber64"
      :vuelidateObj="$v.form.family"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
    <Dropdown
      :title="$lang.gender"
      :label="$lang.gender"
      :placeholder="$lang.gender"
      :value="form.genderSelected"
      name="genderSelected"
      id="genderSelected"
      :vuelidateObj="$v.form.genderSelected"
      :options="genderOptions"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
    <Input
      :title="$lang.national_code"
      :label="$lang.national_code"
      :placeholder="$lang.national_code"
      :value="form.nationalCode"
      name="nationalCode"
      id="nationalCode"
      type="text"
      :minLength="lengthNumber10"
      :maxLength="lengthNumber10"
      :vuelidateObj="$v.form.nationalCode"
      :hasLeftIcon="true"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
    <Input
      :title="$lang.mobile"
      :label="$lang.mobile"
      :placeholder="$lang.mobile"
      :infoTooltip="$lang.nationalCodeIsUsername"
      :value="form.mobile"
      name="mobile"
      id="mobile"
      type="text"
      :minLength="lengthNumber11"
      :maxLength="lengthNumber11"
      :vuelidateObj="$v.form.mobile"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
    <PasswordInputWithRandomPassword
      :title="$lang.password"
      :label="$lang.password"
      :placeholder="$lang.password"
      :infoTooltip="$lang.passwordFormatTooltip"
      :value="form.password"
      name="password"
      id="password"
      type="password"
      autocomplete="new-password"
      :minLength="lengthNumber8"
      :maxLength="lengthNumber32"
      :vuelidateObj="$v.form.password"
      :hasLeftIcon="true"
      class="margin-bottom-24"
      rowClass="width-percent-100"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
  </FormWrapper>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import {
  FormWrapper,
  Dropdown,
  Input,
  PasswordInputWithRandomPassword,
} from "@/components/re-design/forms";
import {
  required,
  minLength,
  maxLength,
  numeric,
} from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { getTermCombo } from "@/services";
import {
  checkPersianCharValidation,
  goToRoute,
  renderEumOptions,
} from "@/shared/utils";
import { addManualParvandehForNewUser } from "@/modules/profile/services";

export default {
  mixins: [inputMixin],
  components: {
    FormWrapper,
    Dropdown,
    Input,
    PasswordInputWithRandomPassword,
  },
  validations() {
    return {
      form: {
        termSelected: {
          required,
        },
        name: {
          required,
          minLength: minLength(this.lengthNumber3),
          maxLength: maxLength(this.lengthNumber64),
        },
        family: {
          required,
          minLength: minLength(this.lengthNumber3),
          maxLength: maxLength(this.lengthNumber64),
        },
        genderSelected: {
          required,
        },
        mobile: {
          required,
          numeric,
          persianCharacters: (val) => !checkPersianCharValidation(val),
          minLength: minLength(this.lengthNumber11),
          maxLength: maxLength(this.lengthNumber11),
        },
        nationalCode: {
          required,
          numeric,
          minLength: minLength(this.lengthNumber10),
          maxLength: maxLength(this.lengthNumber10),
        },
        password: {
          required,
          persianCharacters: (val) => !checkPersianCharValidation(val),
          minLength: minLength(this.lengthNumber8),
          maxLength: maxLength(this.lengthNumber32),
        },
      },
    };
  },
  data() {
    return {
      loading: false,
      form: {
        termSelected: null,
        name: null,
        family: null,
        genderSelected: null,
        mobile: null,
        nationalCode: null,
        password: null,
      },
      termOptions: [],
      termLoading: false,
    };
  },
  computed: {
    genderOptions() {
      return renderEumOptions("gender", true);
    },
  },
  created() {
    this.getTermOptions();
  },
  methods: {
    renderEumOptions,
    goToRoute,
    async getTermOptions() {
      try {
        this.termLoading = true;
        const response = await getTermCombo();
        const { options } = response;
        this.termOptions = options;
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.termLoading = false;
      }
    },
    async handleSubmit() {
      this.$v.$touch();
      if (this.$v.$anyError || this.loading) return;

      try {
        this.loading = true;
        const termCode = pathOr(null, ["code"], this.form.termSelected);
        const name = this.form.name;
        const family = this.form.family;
        const genderId = pathOr(null, ["id"], this.form.genderSelected);
        const mobile = this.form.mobile;
        const nationalCode = this.form.nationalCode;
        const password = this.form.password;
        const response = await addManualParvandehForNewUser(
          termCode,
          name,
          family,
          genderId,
          mobile,
          nationalCode,
          password
        );
        const { message } = response;
        this.$notify.success(message);
        this.backToParvandehList();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.loading = false;
      }
    },
    backToParvandehList() {
      goToRoute("parvandeh-", {}, {}, false);
    },
  },
};
</script>
